import {
  checkFacebookTokenValidity, // Keep using IG action for now
  connectFacebook,
  disconnectFacebook,
  fetchInstagramPages as fetchPages,
  selectFacebookPages,
  selectInstagramAccounts,
  selectInstagramError,
  selectIsConnecting,
  selectIsFacebookConnected,
  selectIsLoading,
  selectSelectedInstagramAccount, // Keep using IG selection state for now
  setSelectedInstagramAccountId
} from '@/common/reducers/InstagramSelectorSlice';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertCircle, AlertTriangle, CheckCircle, Facebook, Instagram, Link2Off, Loader2, RefreshCw, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
// Import both page types
import { FacebookPage, InstagramBusinessAccount, useGetAuthTokensQuery } from '@/common/reducers/backend-api/backend-api';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useWebDispatch, useWebSelector } from '@/website/store/web-store';
import { toast } from 'sonner';

// Define a union type for the page object
type MetaPage = FacebookPage | InstagramBusinessAccount;

interface MetaPageTriggerSelectorProps {
  /** Type of page to select */
  pageType: 'facebook' | 'instagram';
  /** Callback with the selected page object */
  onPageSelected: (page: MetaPage | null) => void;
  /** Saved page object from parent */
  savedPage?: MetaPage | null;
}

// Helper to prevent dropdown close on item click
const blockEvent = (event: React.MouseEvent | Event) => {
  event.preventDefault();
  event.stopPropagation();
};

export const MetaPageTriggerSelector: React.FC<MetaPageTriggerSelectorProps> = ({
  pageType,
  onPageSelected,
  savedPage = null
}) => {
  const dispatch = useWebDispatch();
  const validationDone = useRef(false);

  // Common Redux state
  const isFacebookConnected = useWebSelector(selectIsFacebookConnected);
  const error = useWebSelector(selectInstagramError);
  const isLoading = useWebSelector(selectIsLoading);
  const isConnecting = useWebSelector(selectIsConnecting);

  // Select the appropriate page list based on pageType
  const pages = useWebSelector(pageType === 'facebook' ? selectFacebookPages : selectInstagramAccounts) as MetaPage[];

  // For now, keep using the Instagram selection state from Redux for simplicity, 
  // but only if the type is 'instagram'. Facebook selection might rely more on `savedPage`.
  // Alternatively, create separate Redux states or manage FB selection locally/via parent.
  const selectedAccountFromState = useWebSelector(selectSelectedInstagramAccount);

  // Determine the currently selected page/account ID
  const selectedPageId = (pageType === 'instagram' ? selectedAccountFromState?.id : savedPage?.id) || null;

  // Display page logic: prioritize Redux state (if type matches) then savedPage
  const displayPage = (pageType === 'instagram' && selectedAccountFromState) ? selectedAccountFromState : savedPage;

  // Auth token logic
  const { data: authTokens, isLoading: isAuthTokensLoading } = useGetAuthTokensQuery();
  const facebookAuthTokenId = !isAuthTokensLoading ?
    authTokens?.find(token => token.tokenType === 'facebook' && !token.additionalData?.revoked)?._id
    : undefined;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Define Icon based on pageType
  const Icon = pageType === 'facebook' ? Facebook : Instagram;

  // --- Effects ---

  // Initial validation and fetch
  useEffect(() => {
    const initialize = async () => {
      if (validationDone.current) return;
      const validResult = await dispatch(checkFacebookTokenValidity());
      if (checkFacebookTokenValidity.fulfilled.match(validResult) && validResult.payload.isValid) {
        // Fetch pages only if connected, avoid unnecessary fetches if only rendering saved data
        await dispatch(fetchPages());
      }
      validationDone.current = true;
    };
    initialize();
  }, [dispatch]);

  // Effect to handle initial selection or update from savedPage prop
  useEffect(() => {
    // If connected and have pages...
    if (isFacebookConnected && pages.length > 0) {
      const currentSelectedIdInState = pageType === 'instagram' ? selectedAccountFromState?.id : null; // Only check IG state
      const targetId = savedPage?.id; // ID from the parent/saved node data
      let accountToSelect: MetaPage | undefined = undefined;

      // 1. Prioritize savedPage ID if it exists in the current list
      if (targetId) {
        accountToSelect = pages.find(acc => acc.id === targetId);
      }

      // 2. If saved page not found/valid, or no saved page, default to first in list
      if (!accountToSelect) {
        accountToSelect = pages[0];
      }

      // 3. If type is 'instagram', update Redux state if needed
      if (pageType === 'instagram' && accountToSelect && accountToSelect.id !== currentSelectedIdInState) {
        dispatch(setSelectedInstagramAccountId(accountToSelect.id));
      }

      // 4. Always inform parent if the effective selection differs from savedPage
      // or if there's a selection and no savedPage was provided initially
      if (accountToSelect && (!savedPage || savedPage.id !== accountToSelect.id)) {
        onPageSelected(accountToSelect);
      } else if (!accountToSelect && savedPage) {
        // If no account could be selected (e.g., list empty), but parent had one saved, clear parent
        onPageSelected(null);
      }

    } else if (!isFacebookConnected && savedPage) {
      // If disconnected, ensure parent has the savedPage info (for display)
      // This might be redundant if parent already has it, but ensures consistency
      onPageSelected(savedPage);
    } else if (!isFacebookConnected && !savedPage && selectedAccountFromState && pageType === 'instagram') {
      // Clear selection if disconnected and no saved page exists (only relevant for IG type using Redux state)
      dispatch(setSelectedInstagramAccountId(''));
      onPageSelected(null);
    }
  }, [isFacebookConnected, pages, savedPage, pageType, selectedAccountFromState, dispatch, onPageSelected]);

  // --- Handlers ---

  const handleConnect = async (event?: Event) => {
    if (event) blockEvent(event);
    setDropdownOpen(false);
    const resultAction = await dispatch(connectFacebook());
    if (connectFacebook.fulfilled.match(resultAction)) {
      await dispatch(fetchPages());
      // Selection logic is handled by useEffect
      toast.success("Facebook connected", { description: "Account connected successfully." });
    } else if (connectFacebook.rejected.match(resultAction)) {
      toast.error("Connection failed", { description: resultAction.payload as string || "Failed to connect." });
    }
  };

  const handleDisconnect = async (event?: React.MouseEvent<HTMLButtonElement> | Event) => {
    if (event && 'preventDefault' in event) blockEvent(event);
    setDropdownOpen(false);
    const resultAction = await dispatch(disconnectFacebook(facebookAuthTokenId));
    if (disconnectFacebook.fulfilled.match(resultAction)) {
      onPageSelected(null); // Clear selection in parent
      if (pageType === 'instagram') {
        // Clear IG-specific Redux state if applicable
        dispatch(setSelectedInstagramAccountId(''));
      }
      toast.success("Facebook disconnected", { description: "Account disconnected successfully." });
    } else if (disconnectFacebook.rejected.match(resultAction)) {
      toast.error("Disconnection failed", { description: resultAction.payload as string || "Failed to disconnect." });
    }
  };

  const handlePageChange = (pageId: string) => {
    const newlySelectedPage = pages.find(p => p.id === pageId) || null;
    if (newlySelectedPage) {
      onPageSelected(newlySelectedPage); // Notify parent immediately
      if (pageType === 'instagram') {
        dispatch(setSelectedInstagramAccountId(pageId)); // Update Redux for IG type
      }
    }
    // Don't close dropdown here, let the user see the checkmark
    // setDropdownOpen(false); 
  };

  const handleRefresh = (event: React.MouseEvent<HTMLButtonElement>) => {
    blockEvent(event);
    if (!isLoading) {
      dispatch(fetchPages());
    }
  };

  // --- UI Rendering ---

  const TriggerContent = () => {
    const connectText = `Connect ${pageType === 'facebook' ? 'Facebook' : 'Instagram'}`;
    const selectText = `Select ${pageType === 'facebook' ? 'Page' : 'Account'}`;
    const noItemsText = `No ${pageType === 'facebook' ? 'Pages' : 'Accounts'} Found`;

    if (isConnecting || (isLoading && !isFacebookConnected)) { // Show loading only during initial connect/load
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    if (error && !isFacebookConnected) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    // Use displayPage which prioritizes Redux state (for IG) or savedPage
    if (displayPage) {
      // Check if it's an Instagram account to display username
      const isInstagram = 'username' in displayPage;
      return (
        <div className="flex items-center gap-2 truncate">
          {displayPage.profile_picture_url ? (
            <img src={displayPage.profile_picture_url} alt={displayPage.name} className="h-5 w-5 rounded-full flex-shrink-0" />
          ) : (
            <Avatar className={`h-5 w-5 flex-shrink-0 ${pageType === 'facebook' ? 'bg-blue-100' : 'bg-gradient-to-br from-purple-200 to-orange-200'}`}>
              <AvatarFallback className={`text-xs ${pageType === 'facebook' ? 'bg-blue-200 text-blue-700' : 'bg-purple-200 text-purple-700'}`}>
                <Icon className="h-3 w-3" />
              </AvatarFallback>
            </Avatar>
          )}
          <span className="truncate">{displayPage.name}{isInstagram ? ` (@${(displayPage as InstagramBusinessAccount).username})` : ''}</span>
        </div>
      );
    }
    // Connected but loading pages or no pages found/selected
    if (isFacebookConnected) {
      if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
      if (pages.length === 0 && !error) return <span className="text-gray-500">{noItemsText}</span>;
      if (pages.length > 0 && !displayPage) return <span className="text-orange-500">{selectText}</span>;
      if (error) return <span className="text-red-500">Error Loading</span>; // Error while connected
    }

    // Default / Not Connected state
    return <><Icon className="mr-2 h-4 w-4" /> {connectText}</>;
  };

  return (
    <div
      className="space-y-4 p-4 border rounded-md bg-white shadow-sm"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      <div>
        <h4 className="text-sm font-medium mb-2 text-gray-700">Connected Page</h4>
        <div className="bg-gray-50 border rounded-md p-3 min-h-[60px] flex items-center">
          {displayPage ? (
            <div className="flex items-center gap-3 w-full">
              {displayPage.profile_picture_url ? (
                <img src={displayPage.profile_picture_url} alt={displayPage.name} className="h-8 w-8 rounded-full flex-shrink-0" />
              ) : (
                <Avatar className={`h-8 w-8 flex-shrink-0 ${pageType === 'facebook' ? 'bg-blue-100' : 'bg-gradient-to-br from-purple-200 to-orange-200'}`}>
                  <AvatarFallback className={`text-xs ${pageType === 'facebook' ? 'bg-blue-200 text-blue-700' : 'bg-purple-200 text-purple-700'}`}>
                    <Icon className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1 truncate">
                <div className="font-medium text-sm truncate">{displayPage.name}</div>
                {'username' in displayPage && <div className="text-xs text-slate-500 truncate">@{(displayPage as InstagramBusinessAccount).username}</div>}
              </div>
              {!isFacebookConnected && (
                <Tooltip>
                  <TooltipTrigger> <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 ml-2" /> </TooltipTrigger>
                  <TooltipContent>Connection inactive</TooltipContent>
                </Tooltip>
              )}
            </div>
          ) : (
            <div className="flex items-center text-gray-500 text-sm gap-2">
              <Icon className="h-5 w-5" />
              <span>No {pageType === 'facebook' ? 'Page' : 'Account'} connected</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium mb-2 text-gray-700">Authentication</h4>
        <div className="bg-gray-50 border rounded-md p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Facebook className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium">Facebook Account</span>
            </div>
            <Badge variant={isFacebookConnected ? "default" : "outline"} className={isFacebookConnected ? "bg-green-600 text-white" : ""}>
              {isAuthTokensLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : isFacebookConnected ? "Connected" : "Not Connected"}
            </Badge>
          </div>

          {isFacebookConnected ? (
            <div className="flex items-center justify-between gap-2">
              <Alert className="flex-grow bg-green-50 border-green-200 text-green-700 text-xs py-2 px-3">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription className="ml-2">Authenticated</AlertDescription>
              </Alert>
              {/* Disconnect Button (styled similar to Authenticated Alert) */}
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleDisconnect}
                    disabled={isLoading || isConnecting}
                    className="flex items-center justify-center gap-1.5 h-9 w-auto px-3 text-xs text-red-600 border border-red-300 bg-red-50 rounded-md cursor-pointer hover:bg-red-100 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Disconnect</span> {/* Hide text on very small screens if needed */}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Disconnect Facebook</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : error ? (
            <Alert variant="destructive" className="text-xs py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <Alert className="bg-amber-50 border-amber-200 text-amber-700 text-xs py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Connect your Facebook account to select a {pageType === 'facebook' ? 'Page' : 'Account'}.</AlertDescription>
            </Alert>
          )}

          {!isFacebookConnected &&
            <Button
              className="w-full"
              variant="default"
              size="sm"
              onClick={() => handleConnect()}
              disabled={isConnecting || isAuthTokensLoading}
            >
              {isConnecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Facebook className="mr-2 h-4 w-4" />}
              {isConnecting ? 'Connecting...' : 'Connect Facebook'}
            </Button>
          }
        </div>
      </div>

      {isFacebookConnected && (
        <div>
          <h4 className="text-sm font-medium mb-2 text-gray-700">Select {pageType === 'facebook' ? 'Page' : 'Account'}</h4>
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                ref={triggerRef}
                variant="outline"
                className="w-full justify-between bg-white"
                disabled={isLoading || !isFacebookConnected}
              >
                <span className="truncate flex-1 text-left">
                  {isLoading && !displayPage ? 'Loading...' : displayPage ? <TriggerContent /> : `Select a ${pageType === 'facebook' ? 'Page' : 'Account'}`}
                </span>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-slate-500 ml-2" /> : <RefreshCw className="h-4 w-4 text-slate-500 ml-2" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width]"
              align="start"
            >
              <DropdownMenuLabel className="flex justify-between items-center">
                {pageType === 'facebook' ? 'Facebook Pages' : 'Instagram Accounts'}
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleRefresh} disabled={isLoading}>
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isFacebookConnected && pages.length > 0 && (
                <>
                  <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-gray-500">
                    {pageType === 'facebook' ? 'Facebook Pages' : 'Instagram Accounts'}
                  </DropdownMenuLabel>
                  {pages.map((page) => {
                    // Check if it's an Instagram account to display username
                    const isInstagram = 'username' in page;
                    return (
                      <DropdownMenuItem
                        key={page.id}
                        onSelect={() => handlePageChange(page.id)}
                        className="cursor-pointer flex items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-2 truncate">
                          {page.profile_picture_url ? (<img src={page.profile_picture_url} alt={page.name} className="h-5 w-5 rounded-full flex-shrink-0" />)
                            : (<Avatar className={`h-5 w-5 flex-shrink-0 ${pageType === 'facebook' ? 'bg-blue-100' : 'bg-gradient-to-br from-purple-200 to-orange-200'}`}> <AvatarFallback className={`text-xs ${pageType === 'facebook' ? 'bg-blue-200 text-blue-700' : 'bg-purple-200 text-purple-700'}`}> <Icon className="h-3 w-3" /> </AvatarFallback> </Avatar>)}
                          <span className="truncate">{page.name}{isInstagram ? ` (@${(page as InstagramBusinessAccount).username})` : ''}</span>
                        </div>
                        {selectedPageId === page.id && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                </>
              )}
              {/* Authentication Status/Actions */}
              <div className="px-2 py-1.5">
                <div className="text-xs font-medium text-gray-500 mb-1">Authentication</div>
                {isFacebookConnected ? (
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="border-green-300 bg-green-50 text-green-700 font-normal text-xs flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Authenticated
                    </Badge>
                    {/* Disconnect Button (styled like a badge/status) */}
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={handleDisconnect}
                          disabled={isLoading}
                          className="flex items-center gap-1 text-xs text-red-600 border border-red-300 bg-red-50 rounded-md px-2 py-0.5 cursor-pointer hover:bg-red-100 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                          Disconnect
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Disconnect your Facebook account</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <Button
                    className="w-full"
                    variant="default"
                    size="sm"
                    onClick={() => handleConnect()}
                    disabled={isConnecting || isAuthTokensLoading}
                  >
                    {isConnecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Facebook className="mr-2 h-4 w-4" />}
                    {isConnecting ? 'Connecting...' : 'Connect Facebook'}
                  </Button>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

    </div>
  );
};

// Remove the default export if this file only exports the named component
// export default MetaPageTriggerSelector; 